// File: src/components/CompanyHeader.jsx
// ترويسة الشركة للمستندات المطبوعة (فواتير، سندات، تقارير) — ثنائية اللغة
//   يمين: البيانات بالعربي — الوسط: الشعار — يسار: البيانات بالإنجليزي
//   الحقول الفارغة تُخفى بدل "—"، وإن لم يوجد شعار يظهر رمز بالحرف الأول من اسم المنشأة
import React, { useState } from "react";
import { BRAND } from "./Reports/ReportKit";

const C = { ink: "#111827", muted: "#6b7280", line: "#e5e7eb" };

// أسماء بديلة شائعة للحقول حسب الـ API
const pick = (o, ...keys) => keys.map(k => o?.[k]).find(v => v != null && String(v).trim() !== "");

function Row({ label, value, ltrValue }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", gap: 6, lineHeight: 1.7, fontSize: 12.5 }}>
      <span style={{ color: C.muted, whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ color: C.ink, fontWeight: 600, direction: ltrValue ? "ltr" : undefined, unicodeBidi: "isolate" }}>{value}</span>
    </div>
  );
}

function Monogram({ name, accent }) {
  const letter = (name || "").trim().charAt(0) || "•";
  return (
    <div style={{
      width: 76, height: 76, borderRadius: 18, margin: "0 auto",
      display: "grid", placeItems: "center", fontSize: 34, fontWeight: 900, color: "#fff",
      background: accent, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact",
    }}>
      {letter}
    </div>
  );
}

/**
 * @param company   { name, nameEn, taxNumber, crNumber, address, addressEn, phone, email, logoUrl, ... }
 * @param showLogo  إظهار الشعار (افتراضي true)
 * @param accent    لون الخط السفلي والرمز (افتراضي لون الهوية)
 */
export default function CompanyHeader({ company, showLogo = true, accent = BRAND.primary }) {
  const [logoFailed, setLogoFailed] = useState(false);
  if (!company) return null;

  const nameAr = pick(company, "name", "nameAr", "arabicName");
  const nameEn = pick(company, "nameEn", "englishName");
  const vat = pick(company, "taxNumber", "vatNumber");
  const cr = pick(company, "crNumber", "commercialRegistration", "commercialRegNo", "commercialRegister");
  const addressAr = pick(company, "address", "addressAr");
  const addressEn = pick(company, "addressEn", "address");
  const phone = pick(company, "phone", "mobile");
  const email = pick(company, "email");
  const logo = showLogo && !logoFailed ? pick(company, "logoUrl", "logo") : null;

  return (
    <div className="company-header" style={{ width: "100%", marginBottom: 18, direction: "rtl", color: C.ink }}>
      <div style={{
        display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 20,
        paddingBottom: 12,
      }}>
        {/* يمين — عربي */}
        <div style={{ textAlign: "right", direction: "rtl", minWidth: 0 }}>
          {nameAr && <div style={{ fontWeight: 900, fontSize: 19, lineHeight: 1.35, marginBottom: 4 }}>{nameAr}</div>}
          <Row label="الرقم الضريبي:" value={vat} ltrValue />
          <Row label="السجل التجاري:" value={cr} ltrValue />
          <Row label="العنوان:" value={addressAr} />
          <Row label="الهاتف:" value={phone} ltrValue />
        </div>

        {/* الوسط — الشعار */}
        <div style={{ textAlign: "center", minWidth: 96 }}>
          {logo ? (
            <img
              src={logo}
              alt={nameEn || nameAr || "Logo"}
              onError={() => setLogoFailed(true)}
              style={{ maxWidth: 130, maxHeight: 96, objectFit: "contain", display: "block", margin: "0 auto" }}
            />
          ) : showLogo ? (
            <Monogram name={nameEn || nameAr} accent={accent} />
          ) : null}
        </div>

        {/* يسار — English */}
        <div style={{ textAlign: "left", direction: "ltr", minWidth: 0 }}>
          {nameEn && <div style={{ fontWeight: 900, fontSize: 18, lineHeight: 1.35, marginBottom: 4 }}>{nameEn}</div>}
          <Row label="VAT No:" value={vat} />
          <Row label="CR No:" value={cr} />
          <Row label="Address:" value={addressEn} />
          <Row label="Phone:" value={phone} />
          <Row label="Email:" value={email} />
        </div>
      </div>

      {/* الخط السفلي: لون الهوية + خط رمادي */}
      <div style={{ display: "flex", alignItems: "center", gap: 0, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
        <div style={{ height: 3, width: 90, background: accent, borderRadius: 2 }} />
        <div style={{ height: 1, flex: 1, background: C.line }} />
        <div style={{ height: 3, width: 90, background: accent, borderRadius: 2 }} />
      </div>
    </div>
  );
}